import { Button, InputAdornment, MenuItem, TextField } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaEnvelopeOpenText, FaTerminal } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { Header } from './HomePageLab';

const SUJETS = [
  { value: 'demo',         label: 'Demander une démonstration/essai gratuit' },
  { value: 'audit',        label: 'Audit gratuit (Entreprise)'  },
  { value: 'grandeecole',  label: 'Panel Grande École (Établissement)' },
  { value: 'pilote',       label: 'Lancer un projet pilote'    },
  { value: 'insertion',    label: 'Projet d\'insertion / OPCO'  },
  { value: 'particulier',  label: 'Parcours individuel'         },
  { value: 'partenariat',  label: 'Partenariat / Collaboration' },
  { value: 'presse',       label: 'Relations presse'            },
  { value: 'autre',        label: 'Autre'                       },
];

const CONTEXTES = {
  education:  { titre: 'Pour votre établissement', note: "Vous venez de découvrir ce que Pesacore peut faire pour vos cours et cohortes. La prochaine étape est une conversation sans engagement." },
  entreprise: { titre: 'Pour votre organisation',  note: "L'audit est gratuit. Il dure 45 minutes. Il produit une estimation de ROI personnalisée et un plan pilote sur 20 managers." },
  manifeste:  { titre: 'Vous avez lu le manifeste', note: "La méthodologie vous a interpellé. Parlons de ce que vous voulez transformer." },
  default:    { titre: 'Engageons la conversation', note: "Que vous soyez une institution, une entreprise ou un esprit libre, chaque grande transformation commence par un échange." },
};

const NEXT_STEPS = {
  education:  ['Réponse sous 24h ouvrées', 'Démo planifiée selon vos disponibilités', 'Accès essai 30 jours si pertinent'],
  entreprise: ['Réponse sous 24h ouvrées', 'Appel de qualification (20 min)', 'Audit ROI personnalisé (45 min)', 'Plan pilote sur 20 managers'],
  default:    ['Réponse sous 24h ouvrées', 'Appel de découverte selon vos besoins', 'Proposition adaptée'],
};

const monoStyle  = { fontFamily: "'Roboto Mono', 'Roboto', monospace", letterSpacing: '0.5px' };
const serifStyle = { fontFamily: "'Georgia', 'Times New Roman', serif" };

// Styles MUI adaptés au fond sombre avec plus de contraste
const fieldSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'rgba(255,255,255,0.07)',
    borderRadius: '2px',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'all 0.25s ease',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.10)', borderColor: 'rgb(255, 255, 255)' },
    '&.Mui-focused': { bgcolor: 'rgba(34,211,238,0.04)', borderColor: '#22d3ee', boxShadow: '0 0 0 1px rgba(34,211,238,0.2)' },
  },
  '& .MuiInputLabel-root': {
    ...monoStyle,
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.85)',
    '&.Mui-focused': { color: '#22d3ee' },
    '&.MuiInputLabel-shrink': { fontSize: '0.7rem', transform: 'translate(12px, 6px) scale(0.85)' },
  },
  '& .MuiInputBase-input': {
    ...serifStyle,
    color: '#f7f4ef',
    fontSize: '1rem',
    padding: '1.05rem 0.9rem',
    '&::placeholder': { color: 'rgb(255, 255, 255)', opacity: 1 },
  },
};

const selectSx = {
  ...fieldSx,
  '& .MuiSelect-select': {
    paddingLeft: '0.9rem !important',
    paddingTop: '1.4rem !important',
    paddingBottom: '0.5rem !important',
    ...monoStyle,
    fontSize: '0.9rem',
    color: '#f7f4ef',
  },
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const from    = searchParams.get('from')   || 'default';
  const sujetQS = searchParams.get('sujet')  || '';

  const contexte   = CONTEXTES[from]    || CONTEXTES.default;
  const nextSteps  = NEXT_STEPS[from]   || NEXT_STEPS.default;

  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [sujet, setSujet]     = useState(sujetQS || 'demo');
  const [form, setForm]       = useState({ prenom: '', nom: '', email: '', organisation: '', message: '' });
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth < 768);
  onResize();
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);
  useEffect(() => { if (sujetQS) setSujet(sujetQS); }, [sujetQS]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    setLoading(false);
    setSent(true);
  };

  return (
    <div style={{ background: '#020617', minHeight: '100vh', color: '#e2e8f0', position: 'relative', overflowX: 'hidden' }}>

      {/* Fond : papier millimétré */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(rgba(34,211,238,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_30%_20%,rgba(34,211,238,0.07),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_70%_80%,rgba(245,158,11,0.04),transparent_55%)]" />

      {/* Header */}
      <Header />

      {/* HERO */}
      <section style={{
        padding: isMobile ? '3rem 1.25rem 2rem' : '6rem 2rem 3rem',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.75rem' : '1.5rem',
            marginBottom: '2rem',
            flexWrap: 'wrap',
          }}>
            <div className="h-px w-8 bg-cyan-500/40" />
            <span style={{ ...monoStyle, fontSize: '0.55rem', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.35em' }}>
              Pesacore Architect · Contact
            </span>
            <div className="flex items-center gap-1.5 ml-auto">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span style={{ ...monoStyle, fontSize: '0.5rem', color: 'rgb(255, 255, 255)', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Actif</span>
            </div>
          </div>
          <h1 style={{ ...serifStyle, fontSize: 'clamp(2.25rem, 6vw, 5rem)', fontWeight: 400, lineHeight: 1.1, marginBottom: '1.5rem', maxWidth: '18ch', color: '#f7f4ef' }}>
            {contexte.titre.split(' ').slice(0, -1).join(' ')}{' '}
            <em style={{ color: '#22d3ee' }}>{contexte.titre.split(' ').slice(-1)[0]}</em>.
          </h1>
          <p style={{ ...serifStyle, fontSize: isMobile ? '1rem' : '1.1rem', maxWidth: '48ch', opacity: 0.8, lineHeight: 1.72, color: 'rgba(255, 255, 255, 0.85)' }}>
            {contexte.note}
          </p>
        </motion.div>
      </section>

      {/* FORMULAIRE + INFOS */}
      <section style={{
        padding: isMobile ? '1rem 1.25rem 4rem' : '2rem 2rem 6rem',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.6fr',
          gap: isMobile ? '2.5rem' : '4rem',
          alignItems: 'start',
        }}>

          {/* Colonne gauche */}
          <div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '2.5rem', marginBottom: '3rem' }}>
              <div style={{ ...monoStyle, fontSize: '0.52rem', color: 'rgb(255, 255, 255)', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Localisation</div>
              <p style={{ ...serifStyle, fontSize: '1rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.65)' }}>55 rue Grignan, 13006 Marseille, France<br />Réseau Pesacore Architect</p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <div style={{ ...monoStyle, fontSize: '0.52rem', color: 'rgb(255, 255, 255)', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>Contact direct</div>
              <p style={{ ...serifStyle, fontSize: '1rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.65)' }}>
                admin@pesacore.fr<br />
                +33 (0)7 87 11 20 25
              </p>
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '2.5rem' }}>
              <div style={{ ...monoStyle, fontSize: '0.52rem', color: 'rgb(255, 255, 255)', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>Ce qui se passe après</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {nextSteps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ ...monoStyle, fontSize: '0.55rem', color: '#22d3ee', minWidth: '1.5rem', paddingTop: '0.2rem' }}>{String(i + 1).padStart(2, '0')}</div>
                    <div style={{ ...serifStyle, fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, borderLeft: '1px solid rgba(34,211,238,0.15)', paddingLeft: '0.9rem' }}>{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Colonne droite — formulaire */}
          <motion.div initial={{ opacity: 0, x: isMobile ? 0 : 20 }} animate={{ opacity: 1, x: 0 }}
            style={{
              background: 'rgba(255,255,255,0.04)',
              padding: isMobile ? '2rem 1.25rem' : '3.5rem 3rem',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '4px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              minWidth: 0,
            }}>

            {sent ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
                  <span style={{ fontSize: '1.5rem', color: '#22d3ee' }}>✓</span>
                </div>
                <h2 style={{ ...serifStyle, fontSize: '1.75rem', fontWeight: 400, marginBottom: '1rem', color: '#f7f4ef' }}>
                  Message <em style={{ color: '#22d3ee' }}>reçu</em>.
                </h2>
                <p style={{ ...serifStyle, fontSize: '1rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, marginBottom: '2.5rem', maxWidth: '36ch', margin: '0 auto 2.5rem' }}>
                  Nous reviendrons vers vous sous 24h ouvrées. En attendant, le manifeste est à votre disposition.
                </p>
                <a href="/manifeste" style={{ ...monoStyle, fontSize: '0.62rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#22d3ee', textDecoration: 'none', borderBottom: '1px solid rgba(34,211,238,0.3)', paddingBottom: '2px' }}>
                  Lire le manifeste →
                </a>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1.25rem',
                }}>
                  <TextField fullWidth label="Prénom" variant="filled" required value={form.prenom}
                    onChange={e => setForm({ ...form, prenom: e.target.value })}
                    sx={fieldSx} InputProps={{ disableUnderline: true }} />
                  <TextField fullWidth label="Nom" variant="filled" required value={form.nom}
                    onChange={e => setForm({ ...form, nom: e.target.value })}
                    sx={fieldSx} InputProps={{ disableUnderline: true }} />
                </div>

                <TextField fullWidth label="Email professionnel" variant="filled" type="email" required
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  sx={fieldSx} InputProps={{
                    disableUnderline: true,
                    startAdornment: <InputAdornment position="start"><FaTerminal size={13} style={{ color: '#22d3ee', opacity: 0.6 }} /></InputAdornment>,
                  }} />

                <TextField fullWidth label="Organisation (facultatif)" variant="filled"
                  value={form.organisation} onChange={e => setForm({ ...form, organisation: e.target.value })}
                  sx={fieldSx} InputProps={{ disableUnderline: true }} />

                <TextField select fullWidth label="Objet du message" value={sujet}
                  onChange={e => setSujet(e.target.value)} variant="filled"
                  sx={selectSx} InputProps={{ disableUnderline: true }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#0a0a0f', border: '1px solid rgba(34,211,238,0.15)', borderRadius: '2px', mt: 0.5 } } } }}>
                  {SUJETS.map(s => (
                    <MenuItem key={s.value} value={s.value}
                      sx={{ ...monoStyle, fontSize: '0.88rem', color: '#e2e8f0', '&.Mui-selected': { bgcolor: 'rgba(34,211,238,0.08)' }, '&:hover': { bgcolor: 'rgba(34,211,238,0.04)' } }}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField fullWidth label="Votre message" variant="filled" multiline rows={5} required
                  value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                  sx={fieldSx} InputProps={{
                    disableUnderline: true,
                    startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.8 }}><FaEnvelopeOpenText size={13} style={{ color: '#22d3ee', opacity: 0.6 }} /></InputAdornment>,
                  }} />

                <Button type="submit" fullWidth disabled={loading} sx={{
                  ...monoStyle,
                  py: 2.8,
                  mt: 1,
                  bgcolor: 'rgba(34,211,238,0.10)',
                  color: '#f7f4ef',
                  fontSize: '0.72rem',
                  letterSpacing: '0.8px',
                  fontWeight: 600,
                  borderRadius: '0',
                  border: '1px solid rgba(34,211,238,0.25)',
                  '&:hover': { bgcolor: 'rgba(34,211,238,0.18)', borderColor: 'rgba(34,211,238,0.45)', transform: 'translateY(-1px)' },
                  '&:disabled': { bgcolor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' },
                  transition: 'all 0.3s ease',
                }}>
                  {loading ? 'Transmission en cours…' : 'Envoyer le message'}
                </Button>

                <p style={{ ...monoStyle, fontSize: '0.52rem', color: 'rgba(255, 255, 255, 0.79)', letterSpacing: '0.2em', textAlign: 'center', textTransform: 'uppercase' }}>
                  Données traitées sur serveurs France · RGPD
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{
        padding: isMobile ? '1.5rem 1.25rem' : '2rem 2rem',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: isMobile ? '1rem' : 0,
        textAlign: isMobile ? 'center' : 'left',
        ...monoStyle,
        fontSize: '0.5rem',
        color: 'rgba(255, 255, 255, 0.79)',
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
      }}>
        <span>© Pesacore Architect 2026. Tous droits réservés</span>
        <span>La méthodologie précède la plateforme</span>
        <a href="/" style={{ color: 'rgba(255, 255, 255, 0.79)', textDecoration: 'none' }}>Accueil</a>
      </footer>
    </div>
  );
}